import React, { useState, createElement } from 'react';

import { Icon } from 'js/design_system/Icon';
import PremiumIcon from 'js/premium/PremiumIcon';
import type { LanguageCode } from 'js/translation/util';
import type { Work as LiswpWork } from 'js/liswp/types';

/**
 * The minimal slice of the canonical work shape the TranslationModal needs.
 * The modal only reads `id` (for preview/download/checkout) and `language`
 * (to auto-detect the source language), but we forward what we have so the
 * cast to the liswp `Work` type stays honest.
 */
export interface TranslatableWork {
  id: number;
  title: string;
  slug?: string;
  internalUrl?: string;
  isBookmarked?: boolean;
  language?: string;
  authors: Array<{
    id: number | string;
    displayName: string;
    photo?: string;
    url?: string;
  }>;
  downloadableAttachments: Array<{ downloadUrl: string; id: number }>;
}

interface Props {
  work: TranslatableWork;
  premium: boolean;
  userLanguages: string[];
}

/**
 * Translate button for the canonical work card (Bookmarks / Reading History).
 * Mirrors the single-work-page Translate PDF button: opens the shared
 * TranslationModal, which downloads the translated PDF for premium users and
 * shows the upsell-to-checkout flow for everyone else.
 */
const TranslateButton: React.FC<Props> = ({ work, premium, userLanguages }) => {
  const [translationModalElement, setTranslationModalElement] =
    useState<React.ReactElement | null>(null);

  const handleTranslateClick = () => {
    import('js/translation/TranslationModal').then(
      ({ default: TranslationModal }) => {
        const modalWork = {
          id: work.id,
          title: work.title,
          slug: work.slug ?? `work-${work.id}`,
          internalUrl: work.internalUrl ?? '',
          isBookmarked: work.isBookmarked ?? false,
          language: work.language,
          authors: work.authors.map((author) => ({
            id:
              typeof author.id === 'string'
                ? parseInt(author.id, 10)
                : author.id,
            displayName: author.displayName,
            photo: author.photo,
            url: author.url ?? '',
          })),
          researchInterests: [],
        } as unknown as LiswpWork;

        setTranslationModalElement(
          createElement(TranslationModal, {
            work: modalWork,
            onClose: () => setTranslationModalElement(null),
            premium,
            userLanguages: userLanguages.filter(
              (lng) => typeof lng !== 'undefined',
            ) as LanguageCode[],
            source: 'library-translate-button',
          }),
        );
      },
    );
  };

  return (
    <>
      {/* Matches the card's other secondary actions (Bulk Download, Bookmark),
          which use the bespoke .work-card--action-button-secondary pill rather
          than the design-system Button. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <button
        className="work-card--action-button-secondary"
        onClick={handleTranslateClick}
      >
        {premium ? (
          <Icon name="translate" size={16} style={{ marginRight: 6 }} />
        ) : (
          <PremiumIcon />
        )}
        <div>Translate PDF</div>
      </button>
      {/* Modal renders through a portal, but React replays synthetic events
          along the React owner tree, not the DOM tree — so a modal rendered as
          a JSX child of the button would still re-fire its onClick. Keeping it
          a sibling (as the SWP TranslateButton does) is what prevents that. */}
      {translationModalElement}
    </>
  );
};

export default TranslateButton;
